import React, { Fragment, useState, useEffect, useRef } from 'react';

// Component

import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// Utils

import validators from '@utils/validators/validators';
import { maskValue } from '@utils/miscellaneous/mask.utils';

// Constants

import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

const MaskedInput = props => {
    const {
        inputData: {
            id,
            isInputRequired,
            helperClasses,
            name,
            placeholder,
            value,
            validationType,
            validationErrorMessage,
            valuesToMatch
        },
        inputActions: { handleFieldChange, handleFieldBlur, onFieldFocus },
        label: { text, labelClasses, ariaLabel },
        isErrorShown
    } = props;

    const [localState, setLocalState] = useState({
        currentValue: value,
        isHidden: true,
    });
    const WARNING_TYPE = {
        INCORRECT: 'INCORRECT',
    };

    const warningsConfig = {
        [WARNING_TYPE.INCORRECT]: {
            visibility: false,
            warningText: validationErrorMessage ?? VALIDATION_ERROR_MESSAGES[validationType],
        },
    };

    const [warnings, updateWarnings] = useState(warningsConfig);
    const timeoutRef = useRef(null);

    const { currentValue, isHidden } = localState;

    useEffect(() => {
        if (isHidden) {
            setLocalState(state => ({
                ...state,
                currentValue: value,
            }));
        }
    }, [value, isHidden]);

    useEffect(() => {
        if (isErrorShown) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            updateWarnings(warningsConfig);
        }
    }, [isErrorShown]);

    const showMessage = type => {
        const toggleWarning = visibility =>
            updateWarnings(warnings => ({
                ...warnings,
                [type]: {
                    ...warnings[type],
                    visibility,
                },
            }));

        if (!isErrorShown) {
            toggleWarning(true);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                toggleWarning(false);
                timeoutRef.current = null;
            }, 10 * 1000);
        }
    };

    const handleFocus = (e) => {
        setLocalState({
            currentValue: currentValue,
            isHidden: false,
        });
        onFieldFocus?.(e);
    };

    const handleChange = e => {
        const { value: nextValue } = e.target;

        setLocalState(state => ({
            ...state,
            currentValue: nextValue,
        }));
    };

    const handleBlur = () => {
        let isValid = false;
        if (validators && validators.find(validator => validator.validatorType === VALIDATION_TYPES[validationType])) {
            isValid = validators
                .find(validator => validator.validatorType === VALIDATION_TYPES[validationType])
                .verifier({ value: currentValue, valuesToMatch });
        } else {
            isValid = true;
        }

        if (!isValid) {
            showMessage(WARNING_TYPE.INCORRECT);
        } else if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            updateWarnings(warningsConfig);
        }

        handleFieldChange(isValid ? currentValue : null); 

        setLocalState(state => ({
            currentValue: isValid ? state.currentValue : '',
            isHidden: true,
        }));

        handleFieldBlur(currentValue);
    };

    const getWarnings = () => {
        return Object.keys(warningsConfig).map(type => {
            return (
                <ValidationErrorMessage
                    key={type}
                    isVisible={warnings[type].visibility}
                    messageText={warnings[type].warningText}
                />
            );
        });
    };

    return (
        <Fragment>
            {getWarnings()}
            <div className={`grid-row gi-component gi-input gi-masked_input ${helperClasses && helperClasses.join(' ')}`}>
                <label
                    htmlFor={id}
                    data-testid={`${id}_label`}
                    className={`tablet:grid-col-4 gi-input__label ${labelClasses && labelClasses.join(' ')} required-${isInputRequired}`}
                    aria-label={ariaLabel}
                >
                    {text}
                </label>
                <input
                    id={id}
                    className={'tablet:grid-col-6 gi-input__input'}
                    data-testid={`${id}_input`}
                    name={name}
                    type="text"
                    value={(isHidden && currentValue) ? maskValue(currentValue) : currentValue ?? ''}
                    aria-required={isInputRequired}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                />
            </div>
        </Fragment>
    );
};

export default MaskedInput;
